/**
 * Low-level HTTP client for the sk-bridge-api. Added for the FE↔BE integration.
 *
 * Responsibilities (kept deliberately small):
 *  - Prefix every path with the API base + bridge prefix.
 *  - Inject auth headers (dev-bypass today; one swap-point for real JWT later).
 *  - Parse the BE's closed error envelope `{ code, message }` into a typed
 *    ApiError so callers/UI can branch on `code` without scraping strings.
 *
 * It holds NO app state and dispatches NO actions — higher-level feature
 * modules (src/api/*.ts) call this and hand results to the store.
 */
import { API_BASE, API_PREFIX, DEV_TENANT_ID, USE_PLATFORM_AUTH } from './config';

/** Mirrors the BE closed failure envelope (src/types/errors.ts). */
export class ApiError extends Error {
  code: string;
  status: number;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

export interface RequestOpts {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  // When true, sends x-sk-internal:true so the BE treats the caller as the
  // Softknack (admin) role. Set by callers from the FE role toggle. This is the
  // FE↔BE bridge for the "Softknack internal vs tenant" surfaces.
  internal?: boolean;
  // Per-request extra headers (e.g. Idempotency-Key for write tools).
  headers?: Record<string, string>;
}

/**
 * Build auth headers.
 *
 * Real auth is the platform's httpOnly `accessToken` cookie, sent by the browser
 * because every request below uses `credentials: 'include'` — the token never
 * touches JS. Nothing is added here for it.
 *
 * DEV_TENANT_ID is only sent when USE_PLATFORM_AUTH is off, for local work
 * against a bridge API running with AUTH_DEV_BYPASS=true. x-sk-internal marks
 * the Softknack (admin) surfaces and is orthogonal to how the caller authed.
 */
function authHeaders(internal: boolean | undefined): Record<string, string> {
  const h: Record<string, string> = {};
  if (!USE_PLATFORM_AUTH) {
    h['x-tenant-id'] = DEV_TENANT_ID;
  }
  if (internal) {
    h['x-sk-internal'] = 'true';
  }
  return h;
}

export async function apiFetch<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const url = API_BASE + API_PREFIX + path;
  const init: RequestInit = {
    method: opts.method || 'GET',
    // Sends the platform's httpOnly accessToken cookie cross-origin. The API
    // echoes the specific origin + Allow-Credentials for this to be accepted.
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(opts.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...authHeaders(opts.internal),
      ...(opts.headers || {}),
    },
    ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
  };

  let res: Response;
  try {
    res = await fetch(url, init);
  } catch (networkErr) {
    // Network/CORS failure — surface as a closed code so the UI shows a clean
    // message instead of a raw fetch rejection.
    throw new ApiError(0, 'ERR_NETWORK', `Cannot reach bridge API at ${API_BASE}`);
  }

  // 204/empty-body responses (e.g. some DELETEs) — return undefined as T.
  const text = await res.text();
  const data = text ? safeJson(text) : undefined;

  if (!res.ok) {
    const code = (data && (data as any).code) || 'ERR_UNKNOWN';
    const message = (data && (data as any).message) || res.statusText || 'Request failed';
    throw new ApiError(res.status, code, message);
  }

  return data as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    // Non-JSON success body is unexpected from this API; return the raw text so
    // callers at least see something rather than a thrown parse error.
    return text;
  }
}
